import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Pravo } from '@helfr/domain';
import { api, ApiChyba, nastavPri401 } from '../api/client.js';
import { identifikujUzivatele, resetAnalytiku } from '../lib/analytika.js';
import type { Me } from '../api/types.js';

interface AuthHodnota {
  /** Přihlášený uživatel; null = nepřihlášen; undefined = ještě ověřujeme. */
  me: Me | null | undefined;
  nacitani: boolean;
  maPravo: (pravo: Pravo) => boolean;
  /** Zeď oprávnění: bez finance.read UI finanční sloupce vůbec nevykresluje. */
  maFinance: boolean;
  login: (email: string, heslo: string) => Promise<Me>;
  /** Samoobslužné založení firmy — server rovnou přihlásí (cookie jako login). */
  registrace: (telo: RegistraceTelo) => Promise<Me>;
  logout: () => Promise<void>;
}

export interface RegistraceTelo {
  firmaNazev: string;
  ico?: string | null;
  dic?: string | null;
  adresa?: string | null;
  jmeno: string;
  email: string;
  heslo: string;
}

const AuthContext = createContext<AuthHodnota | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<Me | null> => {
      try {
        // Anonym dostane 200 s null (žádná 401 chyba v konzoli veřejného webu).
        return await api.get<Me | null>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiChyba && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    // Kterýkoli request s 401 (vypršelá session) → uživatel je odhlášen,
    // ProtectedRoute přesměruje na login.
    nastavPri401(() => queryClient.setQueryData(['me'], null));
    return () => nastavPri401(null);
  }, [queryClient]);

  // Analytika: anonymní identita (userId + role + firma) po každém načtení me.
  useEffect(() => {
    if (me) identifikujUzivatele(me);
  }, [me]);

  const hodnota: AuthHodnota = {
    me,
    nacitani: isLoading,
    maPravo: (pravo) => me?.prava.includes(pravo) ?? false,
    maFinance: me?.prava.includes('finance.read') ?? false,
    login: async (email, heslo) => {
      const prihlaseny = await api.post<Me>('/api/auth/login', { email, heslo });
      queryClient.setQueryData(['me'], prihlaseny);
      return prihlaseny;
    },
    registrace: async (telo) => {
      const prihlaseny = await api.post<Me>('/api/registrace', telo);
      queryClient.setQueryData(['me'], prihlaseny);
      return prihlaseny;
    },
    logout: async () => {
      try {
        await api.post('/api/auth/logout', {});
      } finally {
        resetAnalytiku();
        queryClient.setQueryData(['me'], null);
        queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      }
    },
  };

  return <AuthContext.Provider value={hodnota}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthHodnota {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth musí být uvnitř <AuthProvider>');
  return ctx;
}
